// Form access shared by the input sections: the watched values and setters that mark the form dirty (auto-save) and
// report the change as a user edit (the HTML input/change events: manual input mode, solutions invalidation).
import { createContext, useCallback, useContext } from "react";
import { useFormContext, useWatch, type Path, type PathValue } from "react-hook-form";
import type { ScrewConnectorCalculationFormValues } from "../../../http/screwConnector/useAutoSaveScrewConnectorCalculation";

export type ScFormValues = ScrewConnectorCalculationFormValues;
export type ScFormSetter = <K extends keyof ScFormValues & string>(name: K, value: ScFormValues[K]) => void;
export type ScFormPatcher = (patch: Partial<ScFormValues>) => void;

// notified with the names of the fields the user changed
export const ScUserEditContext = createContext<(fields: string[]) => void>(() => undefined);

export function useScForm(): { values: ScFormValues; set: ScFormSetter; apply: ScFormPatcher } {
  const { control, setValue } = useFormContext<ScFormValues>();
  const onUserEdit = useContext(ScUserEditContext);
  // the form is created with complete defaultValues, so the watched object is never partial
  const values = useWatch({ control }) as ScFormValues;
  const write = useCallback(
    (name: string, value: unknown) =>
      setValue(name as Path<ScFormValues>, value as PathValue<ScFormValues, Path<ScFormValues>>, {
        shouldDirty: true,
      }),
    [setValue],
  );
  const set = useCallback<ScFormSetter>(
    (name, value) => {
      write(name, value);
      onUserEdit([name]);
    },
    [write, onUserEdit],
  );
  const apply = useCallback<ScFormPatcher>(
    (patch) => {
      const names = Object.keys(patch);
      for (const name of names) write(name, patch[name as keyof ScFormValues]);
      if (names.length) onUserEdit(names);
    },
    [write, onUserEdit],
  );
  return { values, set, apply };
}
